/**
 * EnrollmentsFilters
 *
 * Filter by provider, payer, status, billing-ready; sort by risk or age.
 * Mantine Select, TextInput, Checkbox. PHI-safe: operational filters only.
 */

import { Checkbox, Group, Select } from '@mantine/core';
import { SearchInput } from '../../../components/SearchInput';
import type { EnrollmentsFilters as EnrollmentsFiltersType, EnrollmentFilterStatus } from '../types';
import { EnrollmentStatusLabels } from '../types';

const statusOptions: { value: EnrollmentFilterStatus; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  ...(Object.entries(EnrollmentStatusLabels) as [EnrollmentFilterStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

const sortOptions: { value: EnrollmentsFiltersType['sortBy']; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'risk', label: 'Risk (blockers first)' },
  { value: 'age', label: 'Age (oldest in stage)' },
];

interface EnrollmentsFiltersProps {
  filters: EnrollmentsFiltersType;
  onChange: (filters: EnrollmentsFiltersType) => void;
}

export function EnrollmentsFilters({ filters, onChange }: EnrollmentsFiltersProps): JSX.Element {
  return (
    <Group gap="md" align="center" wrap="wrap" flex="1">
      <SearchInput
        placeholder="Provider..."
        value={filters.providerSearch}
        onChange={(e) => onChange({ ...filters, providerSearch: e.currentTarget.value })}
        w={280}
      />
      <SearchInput
        placeholder="Payer..."
        value={filters.payerSearch}
        onChange={(e) => onChange({ ...filters, payerSearch: e.currentTarget.value })}
        w={280}
      />
      <Select
        placeholder="Status"
        data={statusOptions}
        value={filters.status}
        onChange={(v) => onChange({ ...filters, status: (v as EnrollmentFilterStatus) ?? 'all' })}
      />
      <Select
        placeholder="Sort by"
        data={sortOptions}
        value={filters.sortBy}
        onChange={(v) => onChange({ ...filters, sortBy: (v as EnrollmentsFiltersType['sortBy']) ?? 'default' })}
      />
      <Checkbox
        label="Billing-ready"
        checked={filters.billingReadyOnly}
        onChange={(e) => onChange({ ...filters, billingReadyOnly: e.currentTarget.checked })}
      />
    </Group>
  );
}
